import axios from 'axios';
import { message } from 'antd';

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_STAGING_API_BASE_URL || process.env.REACT_APP_API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Add authorization header from session storage
    const accessToken = sessionStorage.getItem('access_token');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    console.log('Making request to:', config.url);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

/**
 * Clear all authentication data from both localStorage and sessionStorage
 */
const clearAllAuthData = () => {
  // Clear from sessionStorage
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('id_token');
  sessionStorage.removeItem('token_type');
  sessionStorage.removeItem('token_expires_in');
  sessionStorage.removeItem('token_expiry');
  sessionStorage.removeItem('auth_state');
  sessionStorage.removeItem('auth_nonce');
  sessionStorage.removeItem('auth_redirect');
  
  // Clear from localStorage (in case tokens are stored there)
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('token_type');
  localStorage.removeItem('token_expires_in');
  localStorage.removeItem('token_expiry');
  localStorage.removeItem('auth_state');
  localStorage.removeItem('auth_nonce');
  localStorage.removeItem('auth_redirect');
};

/**
 * Check if error response indicates token expiration or authentication failure
 */
const isTokenExpiredError = (error) => {
  if (!error?.response) return false;
  
  const { status, data } = error.response;
  
  // Check for 401 status
  if (status === 401) return true;
  
  // Check for 400 status with token expiration message
  if (status === 400) {
    const errorMessage = data?.detail || data?.message || JSON.stringify(data || '');
    const lowerMessage = errorMessage.toLowerCase();
    
    // Check for common token expiration messages
    if (
      lowerMessage.includes('token') && 
      (lowerMessage.includes('expired') || 
       lowerMessage.includes('invalid') || 
       lowerMessage.includes('authentication failed') ||
       lowerMessage.includes('token validation failed'))
    ) {
      return true;
    }
  }
  
  return false;
};

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Handle successful responses
    return response;
  },
  async (error) => {
    // Handle token expiration errors (401 or 400 with token expiration message)
    if (isTokenExpiredError(error)) {
      console.log('Token expired or invalid - redirecting to auth');
      
      // Clear all authentication data
      clearAllAuthData();
      
      // Show notification
      message.error('Your session has expired. Please log in again.');
      
      // Redirect to auth page after a short delay
      setTimeout(() => {
        const clientLoginBaseUrl = sessionStorage.getItem('client_base_url');
        if (clientLoginBaseUrl) {
          window.location.href = clientLoginBaseUrl;
        } else {
          window.location.href = '/auth';
        }
      }, 1500);
      
      return Promise.reject(error);
    }

    // Handle other errors
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
      message.error('Access forbidden. You do not have permission to perform this action.');
    }

    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data);
      message.error('Server error. Please try again later.');
    }

    return Promise.reject(error);
  }
);

// Export the configured axios instance as both default and named export
export default axiosInstance;
export { axiosInstance };

// Export a function to update the base URL if needed
export const updateBaseURL = (newBaseURL) => {
  axiosInstance.defaults.baseURL = newBaseURL;
};

// Export a function to add auth headers if needed for specific requests
export const addAuthHeaders = (config) => {
  return {
    ...config,
    withCredentials: true
  };
};

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (isTokenExpiredError(event.reason)) {
    console.log('Unhandled token expiration error detected - redirecting to auth');
    // Clear all authentication data
    clearAllAuthData();
    
    // Show notification
    message.error('Your session has expired. Please log in again.');
    
    // Redirect to auth page
    setTimeout(() => {
      const clientLoginBaseUrl = sessionStorage.getItem('client_base_url');
      if (clientLoginBaseUrl) {
        window.location.href = clientLoginBaseUrl;
      } else {
        window.location.href = '/auth';
      }
    }, 1500);
    
    // Prevent the default error handling
    event.preventDefault();
  }
}); 